/* eslint-disable react/jsx-props-no-spreading */
import React, { useState } from 'react';
import {
  BackNavigationAppBar,
  OutlinedIcons,
} from '@marageti/z4-lib';
import {
  Alert,
  AlertTitle,
  Button,
  Stack,
  Container,
  Grid,
  Typography,
  Box,
  Link as MUILink,
  useTheme,
} from '@mui/material';
import { LoadingButton } from '@mui/lab';
import {
  AddedFailureReason,
  ScreeningResult,
  ScreeningStatus,
  TripRequest,
  TripRequestType,
} from '@marageti/z4-sdk/lib/travel';
import { useConfirm } from 'material-ui-confirm';
import { Link } from 'react-router-dom';
import TripRequestDetailsSidebar from '../../../components/sidebars/trip-request-details-sidebar';
import AddFailureReasonDialog from './add-failure-reason-dialog';
import CriteriaList from './criteria-list';
import ScreeningForm from './screening-form';
import { FailureReasonChip, ScreeningCriteriaMap } from './types';

const {
  OutlinedPlusIcon,
  OutlinedRefreshIcon,
  OutlinedTrashIcon,
} = OutlinedIcons;

type ScreeningAlertsProps = {
  hasSentScreeningMessage: boolean;
  isLegacyTripRequest: boolean;
  onRefresh: () => void;
  refreshRemainingSeconds: number;
  screeningResult: ScreeningResult | undefined;
};

type FailureReasonsProps = {
  canEdit: boolean;
  screeningCriteriaMap?: ScreeningCriteriaMap;
  isCorrectionLoading: boolean;
  isSaving: boolean;
  reasons: FailureReasonChip[];
  screeningCriteriaError: boolean;
  screeningPassed: boolean;
  screeningResultNotFound: boolean;
  canRegenerateDraft: boolean;
  onRegenerateDraft: () => void;
  isRegeneratingDraft: boolean;
  onAdd: () => void;
  onRemove: (code: string) => void;
  onRemoveAll: () => void;
};

const ScreeningAlerts = ({
  hasSentScreeningMessage,
  isLegacyTripRequest,
  onRefresh,
  refreshRemainingSeconds,
  screeningResult,
}: ScreeningAlertsProps) => {
  if (isLegacyTripRequest) {
    return (
      <Alert severity="warning">
        <AlertTitle>
          Legacy Trip
        </AlertTitle>
        This trip request was imported from the D7 platform. Please note that previous screening reasons and email communications are not included here.
      </Alert>
    );
  }

  if (hasSentScreeningMessage) {
    return (
      <Alert severity="info">
        <AlertTitle>
          Trip Previously Screened
        </AlertTitle>
        <span>
          A screening message has already been sent to the traveler.
          Please use messaging to continue communication with the traveler. If you would like to review the screening message, please proceed to the
          {' '}
          <MUILink component={Link} to="../">trip messages.</MUILink>
        </span>
      </Alert>
    );
  }

  if (screeningResult?.status === ScreeningStatus.ScreeningProcessing) {
    return (
      <Alert severity="warning">
        <AlertTitle>
          Still Processing
        </AlertTitle>
        Unfortunately, this trip has not been screened yet, and there is no pre-written message. You may submit a message to screen the traveler.
        <Button
          variant="contained"
          onClick={onRefresh}
          disabled={refreshRemainingSeconds > 0}
        >
          {refreshRemainingSeconds ? `Refresh (${refreshRemainingSeconds})` : 'Refresh'}
        </Button>
      </Alert>
    );
  }

  return null;
};

const FailureReasonsAlerts = ({
  canEdit,
  screeningCriteriaMap,
  isCorrectionLoading,
  isSaving,
  reasons,
  screeningCriteriaError,
  screeningPassed,
  screeningResultNotFound,
  canRegenerateDraft,
  onRegenerateDraft,
  isRegeneratingDraft,
  onAdd,
  onRemove,
  onRemoveAll,
}: FailureReasonsProps) => {
  const confirm = useConfirm();
  const actionsDisabled = isCorrectionLoading || isSaving || isRegeneratingDraft;

  const handleRemoveAll = async () => {
    try {
      await confirm({
        title: 'Remove all Failure Reasons?',
        description: 'This action can not be undone.',
        confirmationText: 'Remove',
        cancellationText: 'Cancel',
        confirmationButtonProps: {
          color: 'primary',
          variant: 'contained',
        },
      });
      onRemoveAll();
    } catch {
      // Confirmation cancelled
    }
  };

  return (
    <div>
      <Stack
        alignItems={{ xs: 'flex-start', desktop: 'center' }}
        direction={{ xs: 'column', desktop: 'row' }}
        gap={1}
        justifyContent="space-between"
      >
        <Typography variant="bodyBold">
          Failure Reasons
        </Typography>
        <Stack direction="row" flexWrap="wrap" gap={1} useFlexGap>
          {canEdit && (
            <>
              <Button
                disabled={actionsDisabled}
                endIcon={<OutlinedPlusIcon />}
                onClick={onAdd}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                }}
                variant="contained"
              >
                Add
              </Button>
              <Button
                color="error"
                disabled={actionsDisabled || reasons.length === 0}
                endIcon={<OutlinedTrashIcon />}
                onClick={handleRemoveAll}
                size="small"
                sx={{
                  flexShrink: 0,
                  fontSize: '13px',
                  whiteSpace: 'nowrap',
                  '& .MuiButton-endIcon': {
                    display: { xs: 'none', desktop: 'inherit' },
                  },
                }}
                variant="contained"
              >
                Remove all
              </Button>
            </>
          )}
          {canRegenerateDraft && (
            <LoadingButton
              disabled={actionsDisabled}
              endIcon={<OutlinedRefreshIcon />}
              loading={isRegeneratingDraft}
              onClick={onRegenerateDraft}
              size="small"
              sx={{
                flexShrink: 0,
                fontSize: '13px',
                whiteSpace: 'nowrap',
                '& .MuiButton-endIcon': {
                  display: { xs: 'none', desktop: 'inherit' },
                },
              }}
              variant="outlined"
            >
              Regenerate draft
            </LoadingButton>
          )}
        </Stack>
      </Stack>
      {screeningCriteriaError && (
        <Alert severity="error">
          <AlertTitle>
            Screening Error
          </AlertTitle>
          We encountered an internal error that prevented the AI screening from being performed on this trip request. Please manually review the request and proceed accordingly.
        </Alert>
      )}
      {screeningResultNotFound && (
        <Typography>
          No automated screening has been performed.
        </Typography>
      )}
      {(reasons.length > 0 || isCorrectionLoading) && (
        <Box mt={1.75} mb={0.25}>
          <CriteriaList
            canEdit={canEdit}
            screeningCriteriaMap={screeningCriteriaMap}
            isCorrectionLoading={isCorrectionLoading}
            isSaving={isSaving || isRegeneratingDraft}
            onRemove={onRemove}
            reasons={reasons}
          />
        </Box>
      )}
      {screeningPassed && reasons.length === 0 && !isCorrectionLoading && (
        <Alert severity="success">
          <AlertTitle>
            Screening Passed
          </AlertTitle>
          All criteria passed during automated screening.
        </Alert>
      )}
    </div>
  );
};

type ScreenViewProps = {
  isLoading: boolean;
  isError: boolean;
  tripRequest: TripRequest | undefined;
  screeningResult: ScreeningResult | undefined;
  screeningResultNotFound: boolean;
  hasSentScreeningMessage: boolean;
  onSubmit: (data: any) => void;
  onCancel: () => void;
  submissionError: boolean;
  refreshScreeningResultRemainingSeconds: number;
  refreshScreeningResult: () => void;
  defaultValues: {
    assignedTo: string | undefined;
    message: string | undefined;
  };
  canEditFailureReasons: boolean;
  screeningCriteriaMap?: ScreeningCriteriaMap;
  failureReasons: FailureReasonChip[];
  passedReasons: string[];
  isCorrectionLoading: boolean;
  isSavingFailureReasons: boolean;
  onAddFailureReasons: (drafts: AddedFailureReason[]) => Promise<boolean>;
  onRemoveFailureReason: (code: string) => void;
  onRemoveAllFailureReasons: () => void;
  canRegenerateDraft: boolean;
  onRegenerateDraft: () => void;
  isRegeneratingDraft: boolean;
  draftResetKey: number;
};

const ScreenView = ({
  isLoading,
  isError,
  tripRequest,
  screeningResult,
  screeningResultNotFound,
  hasSentScreeningMessage,
  onSubmit,
  onCancel,
  submissionError,
  refreshScreeningResultRemainingSeconds,
  refreshScreeningResult,
  defaultValues,
  canEditFailureReasons,
  screeningCriteriaMap,
  failureReasons,
  passedReasons,
  isCorrectionLoading,
  isSavingFailureReasons,
  onAddFailureReasons,
  onRemoveFailureReason,
  onRemoveAllFailureReasons,
  canRegenerateDraft,
  onRegenerateDraft,
  isRegeneratingDraft,
  draftResetKey,
}: ScreenViewProps) => {
  const theme = useTheme();
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);

  const handleAddFailureReasons = async (drafts: AddedFailureReason[]) => {
    const saved = await onAddFailureReasons(drafts);
    if (saved) {
      setIsAddDialogOpen(false);
    }
  };

  return (
    <>
      <BackNavigationAppBar
        primary="Screen"
        defaultPath=".."
      />
      <Container maxWidth="xl">
        <Grid container spacing={6}>
          <Grid
            item
            xs={12}
            md={4}

          >
            <Box
              sx={{
                paddingRight: 2,
                paddingBottom: 3,
                [theme.breakpoints.up('desktop')]: {
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.mixins.toolbar.minHeight}px - 6px)`,
                },
              }}
            >
              {tripRequest && <TripRequestDetailsSidebar tripRequestId={tripRequest.id} />}
            </Box>
          </Grid>
          <Grid
            item
            xs={12}
            md={8}
          >
            <Box
              sx={{
                paddingRight: 2,
                paddingBottom: 3,
                [theme.breakpoints.up('desktop')]: {
                  overflowY: 'auto',
                  overscrollBehavior: 'contain',
                  height: `calc(100vh - ${theme.mixins.toolbar.minHeight}px - ${theme.mixins.toolbar.minHeight}px - 6px)`,
                },
              }}
            >
              {isError && (
                <Alert severity="error">
                  <AlertTitle>
                    There was an error loading screening information
                  </AlertTitle>
                  Please try again later.
                </Alert>
              )}

              {isLoading && !isError ? null : (
                <Stack spacing={4}>
                  <ScreeningAlerts
                    hasSentScreeningMessage={hasSentScreeningMessage}
                    isLegacyTripRequest={tripRequest?.tripRequestType === TripRequestType.Legacy}
                    onRefresh={refreshScreeningResult}
                    refreshRemainingSeconds={refreshScreeningResultRemainingSeconds}
                    screeningResult={screeningResult}
                  />

                  {screeningResult?.status !== ScreeningStatus.ScreeningProcessing && (
                    <>
                      <FailureReasonsAlerts
                        canEdit={canEditFailureReasons}
                        screeningCriteriaMap={screeningCriteriaMap}
                        isCorrectionLoading={isCorrectionLoading}
                        isSaving={isSavingFailureReasons}
                        onAdd={() => setIsAddDialogOpen(true)}
                        onRemove={onRemoveFailureReason}
                        onRemoveAll={onRemoveAllFailureReasons}
                        reasons={failureReasons}
                        screeningCriteriaError={screeningResult?.hasErrors || false}
                        screeningPassed={screeningResult?.allPassed || false}
                        screeningResultNotFound={screeningResultNotFound}
                        canRegenerateDraft={canRegenerateDraft}
                        onRegenerateDraft={onRegenerateDraft}
                        isRegeneratingDraft={isRegeneratingDraft}
                      />

                      <AddFailureReasonDialog
                        screeningCriteriaMap={screeningCriteriaMap}
                        excludedCodes={failureReasons.map((reason) => reason.code)}
                        isSaving={isSavingFailureReasons || isRegeneratingDraft}
                        key={isAddDialogOpen ? 'open' : 'closed'}
                        onClose={() => setIsAddDialogOpen(false)}
                        onSubmit={handleAddFailureReasons}
                        open={isAddDialogOpen}
                        passedReasons={passedReasons}
                      />
                    </>
                  )}

                  {!hasSentScreeningMessage && (
                    <ScreeningForm
                      onSubmit={onSubmit}
                      onCancel={onCancel}
                      error={submissionError}
                      defaultValues={defaultValues}
                      draftResetKey={draftResetKey}
                    />
                  )}
                </Stack>
              )}
            </Box>
          </Grid>
        </Grid>
      </Container>
    </>
  );
};

export default ScreenView;
